import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import ConversationEntry from './ConversationEntry';
import { ConversationCategory } from '../types/conversation';

const ConversationFeed: React.FC = () => {
  const { entries } = useSelector((state: RootState) => state.conversation);
  const feedRef = useRef<HTMLDivElement>(null);

  // New Inquiries are displayed in the top NewInquiryBar — exclude them here
  const feedEntries = entries.filter(
    (e) => e.category !== ConversationCategory.NEW_INQUIRY
  );

  // Manual scroll to bottom function
  const scrollToBottom = () => {
    if (feedRef.current) {
      feedRef.current.scrollTo({
        top: feedRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="flex-1 bg-white border-2 border-blue-200 rounded-2xl mx-4 flex flex-col min-h-0">
      {/* Header */}
      <div className="bg-blue-50 px-6 py-1 border-b border-blue-200 rounded-t-2xl flex-shrink-0 flex items-center">
        <div className="flex items-center space-x-2">
          <span className="text-xl">💬</span>
          <h2 className="text-sm font-semibold text-gray-800">Live Conversation Feed</h2>
        </div>
      </div>

      {/* Feed Content - This is the only scrollable part */}
      <div
        ref={feedRef}
        className="flex-1 overflow-y-auto p-3 space-y-2"
        style={{ minHeight: 0, scrollBehavior: 'smooth' }}
      >
        {feedEntries.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500">
              <div className="text-6xl mb-4">🎯</div>
              <h3 className="text-lg font-medium mb-2">No conversations detected yet</h3>
              <p className="text-sm">Start a conversation and questions will appear here automatically</p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {feedEntries.map((entry) => (
              <ConversationEntry key={entry.id} entry={entry} />
            ))}
            {/* Add margin bottom to ensure latest entry is fully visible */}
            <div className="h-3" />
          </div>
        )}
      </div>

      {/* Footer with scroll button and count */}
      <div className="bg-gray-50 px-4 py-2 border-t border-gray-200 rounded-b-2xl flex-shrink-0 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {feedEntries.length > 0
            ? `${feedEntries.length} conversation${feedEntries.length === 1 ? '' : 's'} detected`
            : ''
          }
        </div>
        <button
          onClick={scrollToBottom}
          className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-lg shadow transition-colors flex items-center space-x-1 text-sm font-medium"
        >
          <span>Scroll To Bottom</span>
          <span>↓</span>
        </button>
      </div>
    </div>
  );
};

export default ConversationFeed;